// src/pages/Dashboard.js
import React, { useState, useEffect, useRef } from 'react';
import ReportWriter from '../components/ReportWriter';
import SonoVault from '../components/SonoVault';
import SonoShare from '../components/SonoShare';
import CustomizeTemplate from '../components/CustomizeTemplate';
import SavedReports from '../components/SavedReports';
import AdminDashboard from '../components/AdminDashboard';
import UserProfile from '../components/UserProfile';
import SonoAI from '../components/SonoAI';
import Settings from './Settings';
import Analytics from '../components/Analytics';
import { useNotifications } from '../context/NotificationContext';

const Dashboard = ({
  activeTab,
  setActiveTab,
  userProfile,
  onLogout,
  vaultFiles,
  onAddFiles,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [historyStack, setHistoryStack] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isSplitView, setIsSplitView] = useState(false);
  const [splitViewTab, setSplitViewTab] = useState('templates');

  const notifRef = useRef(null);
  const userRef = useRef(null);

  const { unreadCount, notifications, markRead, markAllRead } =
    useNotifications();

  const isAdmin =
    userProfile?.role === 'ADMIN' ||
    userProfile?.role === 'SUPER_ADMIN' ||
    userProfile?.role === 'HOSPITAL_ADMIN' ||
    userProfile?.role === 'admin';

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifDropdown(false);
      }
      if (userRef.current && !userRef.current.contains(e.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleNetworkStatus = () => setIsOffline(!isOffline);
  const toggleMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const scrollToTop = () => {
    const workspace = document.querySelector('.workspace');
    if (workspace) workspace.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = (tab) => {
    if (tab === activeTab) {
      setIsMobileMenuOpen(false);
      return;
    }
    setHistoryStack((prev) => [...prev, activeTab]);
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
    setShowNotifDropdown(false);
    setShowUserDropdown(false);
    if (tab !== 'report') setIsSplitView(false);
    scrollToTop();
  };

  const handleBack = () => {
    if (historyStack.length > 0) {
      const prevTab = historyStack[historyStack.length - 1];
      setHistoryStack((prev) => prev.slice(0, -1));
      setActiveTab(prevTab);
    } else {
      setActiveTab('report');
    }
    scrollToTop();
  };

  // ─── Sidebar nav item metadata ───
  const NAV_ITEMS = [
    {
      id: 'report',
      label: 'New Report',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      id: 'templates',
      label: 'Customize Template',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
    },
    {
      id: 'saved',
      label: 'Saved Reports',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      ),
    },
    {
      id: 'vault',
      label: 'Sono Vault',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
    },
    {
      id: 'share',
      label: 'Sono Share',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
      ),
    },
    {
      id: 'sono-ai',
      label: 'Sono AI',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    ...(isAdmin
      ? [
          {
            id: 'admin',
            label: 'Admin',
            icon: (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            ),
          },
        ]
      : []),
    {
      id: 'analytics',
      label: 'Analytics',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
        </svg>
      ),
    },
  ];

  const FOOTER_ITEMS = [
    {
      id: 'settings',
      label: 'Settings',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
    {
      id: 'help',
      label: 'Help & Support',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.03-2 3.772-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  ];

  const renderNavItem = (item) => {
    const isActive = activeTab === item.id;
    return (
      <div
        key={item.id}
        onClick={() => handleNavClick(item.id)}
        className={`nav-item ${isActive ? 'active' : ''}`}
      >
        <span className="nav-item-icon">{item.icon}</span>
        <span className="nav-item-label">{item.label}</span>
      </div>
    );
  };

  return (
    <div className="app-container fade-in">
      {/* ═════════════════════════════════════════════════════════════
          COMPACT SIDEBAR — 240px wide, tight nav items so all buttons
          (including Analytics) fit without scrolling on standard
          laptop/desktop screens.
         ═════════════════════════════════════════════════════════════ */}
      <style>{`
        /* ---------- SIDEBAR SHELL ---------- */
        .app-container > aside.sidebar,
        aside.sidebar {
          display: flex !important;
          flex-direction: column !important;
          width: 240px !important;
          height: 100vh !important;
          max-height: 100vh !important;
          background-color: #ffffff !important;
          color: #000000 !important;
          padding: 0 !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
          border-right: 1px solid #e2e8f0 !important;
          flex-shrink: 0 !important;
        }

        /* ---------- BRAND HEADER (compact) ---------- */
        .app-container > aside.sidebar > .brand,
        aside.sidebar > .brand {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          gap: 10px !important;
          padding: 12px 14px 10px 14px !important;
          margin: 0 !important;
          flex-shrink: 0 !important;
          border-bottom: 1px solid #f1f5f9 !important;
          color: #000000 !important;
        }
        .app-container > aside.sidebar > .brand img,
        aside.sidebar > .brand img {
          width: 30px !important;
          height: 30px !important;
          object-fit: contain !important;
        }
        .app-container > aside.sidebar > .brand span,
        aside.sidebar > .brand span {
          color: #000000 !important;
          font-size: 1.05rem !important;
          font-weight: 800 !important;
        }

        /* ---------- ONLINE MODE BUTTON (compact) ---------- */
        .app-container > aside.sidebar > .online-mode-btn,
        aside.sidebar > .online-mode-btn {
          flex-shrink: 0 !important;
          margin: 10px 12px 8px 12px !important;
          padding: 7px 12px !important;
          font-size: 0.8rem !important;
          font-weight: 700 !important;
          border-radius: 8px !important;
          min-height: 32px !important;
          color: #ffffff !important;
          border: none !important;
          cursor: pointer !important;
          width: auto !important;
          box-shadow: none !important;
        }

        /* ---------- NAV LIST (compact) ---------- */
        .app-container > aside.sidebar > .nav-list,
        aside.sidebar > .nav-list {
          flex: 1 1 auto !important;
          min-height: 0 !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
          padding: 4px 10px !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
          margin: 0 !important;
          background: transparent !important;
        }
        .app-container > aside.sidebar > .nav-list::-webkit-scrollbar,
        aside.sidebar > .nav-list::-webkit-scrollbar {
          width: 4px;
        }
        .app-container > aside.sidebar > .nav-list::-webkit-scrollbar-thumb,
        aside.sidebar > .nav-list::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 4px;
        }

        /* ---------- FOOTER (compact) ---------- */
        .app-container > aside.sidebar > .sidebar-footer-menu,
        aside.sidebar > .sidebar-footer-menu {
          flex: 0 0 auto !important;
          flex-shrink: 0 !important;
          margin-top: 0 !important;
          padding: 6px 10px 10px 10px !important;
          border-top: 1px solid #f1f5f9 !important;
          background: transparent !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 4px !important;
          z-index: 2 !important;
        }

        /* ═══════════════════════════════════════════════════════════
           NAV ITEM — compact, soft modern (no outline)
           ═══════════════════════════════════════════════════════════ */
        .app-container > aside.sidebar .nav-item,
        aside.sidebar .nav-item {
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
          padding: 7px 10px !important;
          border-radius: 8px !important;
          cursor: pointer !important;
          font-size: 0.82rem !important;
          font-weight: 500 !important;
          line-height: 1.15 !important;
          min-height: 32px !important;
          background-color: #f8fafc !important;
          border: none !important;
          transition: background-color 0.15s, color 0.15s !important;
          box-sizing: border-box !important;
        }

        .app-container > aside.sidebar .nav-item,
        aside.sidebar .nav-item,
        .app-container > aside.sidebar .nav-item *,
        aside.sidebar .nav-item * {
          color: #0f172a !important;
        }

        .app-container > aside.sidebar .nav-item .nav-item-label,
        aside.sidebar .nav-item .nav-item-label {
          color: #0f172a !important;
          font-weight: 500 !important;
          font-size: 0.82rem !important;
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
        }

        .app-container > aside.sidebar .nav-item .nav-item-icon,
        aside.sidebar .nav-item .nav-item-icon,
        .app-container > aside.sidebar .nav-item .nav-item-icon *,
        aside.sidebar .nav-item .nav-item-icon * {
          color: #3b82f6 !important;
          display: inline-flex !important;
          align-items: center !important;
          flex-shrink: 0 !important;
        }

        /* ---------- ACTIVE ---------- */
        .app-container > aside.sidebar .nav-item.active,
        aside.sidebar .nav-item.active {
          background-color: #eff6ff !important;
        }
        .app-container > aside.sidebar .nav-item.active,
        aside.sidebar .nav-item.active,
        .app-container > aside.sidebar .nav-item.active *,
        aside.sidebar .nav-item.active * {
          color: #2563eb !important;
        }
        .app-container > aside.sidebar .nav-item.active .nav-item-label,
        aside.sidebar .nav-item.active .nav-item-label {
          color: #2563eb !important;
          font-weight: 600 !important;
        }
        .app-container > aside.sidebar .nav-item.active .nav-item-icon,
        aside.sidebar .nav-item.active .nav-item-icon,
        .app-container > aside.sidebar .nav-item.active .nav-item-icon *,
        aside.sidebar .nav-item.active .nav-item-icon * {
          color: #2563eb !important;
        }

        /* ---------- HOVER (inactive) ---------- */
        .app-container > aside.sidebar .nav-item:not(.active):hover,
        aside.sidebar .nav-item:not(.active):hover {
          background-color: #e2e8f0 !important;
        }
        .app-container > aside.sidebar .nav-item:not(.active):hover,
        aside.sidebar .nav-item:not(.active):hover,
        .app-container > aside.sidebar .nav-item:not(.active):hover *,
        aside.sidebar .nav-item:not(.active):hover * {
          color: #0f172a !important;
        }
        .app-container > aside.sidebar .nav-item:not(.active):hover .nav-item-icon,
        aside.sidebar .nav-item:not(.active):hover .nav-item-icon,
        .app-container > aside.sidebar .nav-item:not(.active):hover .nav-item-icon *,
        aside.sidebar .nav-item:not(.active):hover .nav-item-icon * {
          color: #1d4ed8 !important;
        }

        /* ---------- SPLIT VIEW HIDING ---------- */
        .app-container > aside.sidebar[style*="display: none"] {
          display: none !important;
        }

        /* ---------- MOBILE SLIDE-IN ---------- */
        @media (max-width: 768px) {
          .app-container > aside.sidebar.mobile-visible,
          aside.sidebar.mobile-visible {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 260px !important;
            max-width: 85vw !important;
            height: 100vh !important;
            z-index: 10000 !important;
            box-shadow: 4px 0 25px rgba(0, 0, 0, 0.15) !important;
            animation: sidebarSlideIn 0.25s ease forwards;
          }
        }
        @keyframes sidebarSlideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>

      {isSplitView && (
        <style>{`
          .workspace > div:first-child > div > div:nth-of-type(2) {
            display: none !important;
          }
          .workspace > div:first-child > div > div:nth-of-type(1) {
            flex: 1 !important;
          }
        `}</style>
      )}

      {/* ─── Mobile Top Header ─── */}
      <div className="mobile-header">
        <div
          className="brand-mobile"
          style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
        >
          <img
            src={process.env.PUBLIC_URL + '/echionLOGO.png'}
            alt="Echion Logo"
            style={{
              height: '30px',
              objectFit: 'contain',
              borderRadius: '50%',
            }}
          />
          ECHION HEALTH
        </div>
        <button className="hamburger-btn" onClick={toggleMenu}>
          <svg
            viewBox="0 0 24 24"
            width="28"
            height="28"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* ─── Main Sidebar ─── */}
      <aside
        className={`sidebar ${isMobileMenuOpen ? 'mobile-visible' : ''}`}
        style={isSplitView ? { display: 'none' } : undefined}
      >
        <div className="brand">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img
              src={process.env.PUBLIC_URL + '/echionLOGO.png'}
              alt="Echion Health"
            />
            <span>Echion</span>
          </div>

          <button
            className="close-menu-btn"
            onClick={() => setIsMobileMenuOpen(false)}
            style={{
              background: '#f1f5f9',
              border: 'none',
              color: '#000000',
              fontSize: '1.1rem',
              width: '26px',
              height: '26px',
              borderRadius: '6px',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              padding: 0,
            }}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        <button
          className="online-mode-btn"
          onClick={toggleNetworkStatus}
          style={{ backgroundColor: isOffline ? '#ef4444' : '#10b981' }}
        >
          {isOffline ? 'Currently Offline' : 'Online Mode'}
        </button>

        <div className="nav-list">
          {NAV_ITEMS.map((item) => renderNavItem(item))}
        </div>

        <div className="sidebar-footer-menu">
          {FOOTER_ITEMS.map((item) => renderNavItem(item))}
        </div>
      </aside>

      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            zIndex: 9999,
            backdropFilter: 'blur(2px)',
          }}
        ></div>
      )}

      <main
        className="main-content"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <div
          className="top-bar"
          style={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2>
            {activeTab === 'report'
              ? 'New Report'
              : activeTab === 'templates'
              ? 'Customize Template'
              : activeTab === 'saved'
              ? 'Saved Reports'
              : activeTab === 'sono-ai'
              ? 'Sono AI Generator'
              : activeTab === 'admin'
              ? 'Admin Dashboard'
              : activeTab === 'profile'
              ? 'User Profile'
              : activeTab === 'settings'
              ? 'Settings'
              : activeTab === 'analytics'
              ? 'Analytics Dashboard'
              : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            {activeTab === 'report' && (
              <button
                onClick={() => setIsSplitView(!isSplitView)}
                style={{
                  background: isSplitView ? '#ef4444' : 'white',
                  color: isSplitView ? 'white' : '#0f172a',
                  border: isSplitView ? 'none' : '1px solid #cbd5e1',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                }}
              >
                {isSplitView ? (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Disable Split Mode
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 4H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zM12 4v16" />
                    </svg>
                    Split View
                  </>
                )}
              </button>
            )}

            {isOffline && (
              <span
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                }}
              >
                OFFLINE
              </span>
            )}

            <div style={{ position: 'relative' }} ref={notifRef}>
              <button
                onClick={() => {
                  setShowNotifDropdown(!showNotifDropdown);
                  setShowUserDropdown(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: '5px',
                }}
              >
                🔔
                {unreadCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      background: 'red',
                      color: 'white',
                      fontSize: '0.6rem',
                      padding: '2px 5px',
                      borderRadius: '50%',
                    }}
                  >
                    {unreadCount}
                  </span>
                )}
              </button>
              {showNotifDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '40px',
                    width: '300px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                  }}
                >
                  <div
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #eee',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <strong style={{ fontSize: '0.9rem' }}>Notifications</strong>
                    <button
                      onClick={markAllRead}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: '#3498db',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                      }}
                    >
                      Mark all read
                    </button>
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => markRead(n.id)}
                        style={{
                          padding: '10px',
                          borderBottom: '1px solid #f5f5f5',
                          background: n.read ? 'white' : '#f0f8ff',
                          cursor: 'pointer',
                        }}
                      >
                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#333' }}>
                          {n.message}
                        </p>
                        <small style={{ color: '#888', fontSize: '0.7rem' }}>
                          {n.time}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ position: 'relative' }} ref={userRef}>
              <div
                onClick={() => {
                  setShowUserDropdown(!showUserDropdown);
                  setShowNotifDropdown(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                }}
              >
                <span className="user-name-display">
                  {userProfile?.displayName || userProfile?.username || 'User'}
                </span>
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#3498db',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  {(userProfile?.email?.[0] || 'U').toUpperCase()}
                </div>
                <span style={{ fontSize: '0.8rem' }}>▼</span>
              </div>
              {showUserDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '45px',
                    width: '180px',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                  }}
                >
                  <div
                    onClick={() => {
                      handleNavClick('profile');
                      setShowUserDropdown(false);
                    }}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #eee',
                      fontSize: '0.9rem',
                      color: '#333',
                    }}
                  >
                    My Profile
                  </div>
                  {isAdmin && (
                    <div
                      onClick={() => {
                        handleNavClick('admin');
                        setShowUserDropdown(false);
                      }}
                      style={{
                        padding: '12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        fontSize: '0.9rem',
                        color: '#333',
                      }}
                    >
                      Admin Dashboard
                    </div>
                  )}
                  <div
                    onClick={() => {
                      onLogout();
                      setShowUserDropdown(false);
                    }}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      color: '#e74c3c',
                      fontSize: '0.9rem',
                    }}
                  >
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="content-area"
          style={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div
            className="workspace"
            style={{
              flex: 1,
              paddingBottom: '40px',
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'grid',
              gridTemplateColumns: isSplitView ? '1fr 1fr' : '1fr',
              gap: '0px',
            }}
          >
            <div style={{ minWidth: 0 }}>
              {activeTab === 'report' && (
                <ReportWriter userProfile={userProfile} isSplitView={isSplitView} />
              )}
              {activeTab === 'templates' && (
                <CustomizeTemplate onBack={handleBack} />
              )}
              {activeTab === 'saved' && (
                <SavedReports userProfile={userProfile} onBack={handleBack} />
              )}
              {activeTab === 'vault' && (
                <SonoVault
                  files={vaultFiles}
                  onAddFiles={onAddFiles}
                  onBack={handleBack}
                />
              )}
              {activeTab === 'share' && (
                <SonoShare userProfile={userProfile} onBack={handleBack} />
              )}
              {activeTab === 'sono-ai' && <SonoAI userProfile={userProfile} />}
              {activeTab === 'admin' &&
                (isAdmin ? (
                  <AdminDashboard userProfile={userProfile} />
                ) : (
                  <div
                    style={{
                      padding: '50px',
                      textAlign: 'center',
                      color: '#e74c3c',
                    }}
                  >
                    <h3>Access Denied</h3>
                  </div>
                ))}
              {activeTab === 'profile' && <UserProfile user={userProfile} />}
              {activeTab === 'viewer' && (
                <div style={{ padding: '50px', textAlign: 'center' }}>
                  <h3>Sono Viewer Loading...</h3>
                </div>
              )}
              {activeTab === 'analytics' && <Analytics />}
              {activeTab === 'settings' && <Settings />}
              {activeTab === 'help' && (
                <div style={{ padding: '50px', textAlign: 'center' }}>
                  <h3>Help &amp; Support Loading...</h3>
                </div>
              )}
            </div>

            {isSplitView && activeTab === 'report' && (
              <div
                style={{
                  borderLeft: '1px solid #e2e8f0',
                  background: '#fcfcfc',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  overflow: 'hidden',
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    padding: '10px 20px',
                    borderBottom: '1px solid #e2e8f0',
                    background: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                    zIndex: 10,
                  }}
                >
                  <span
                    style={{
                      fontWeight: '600',
                      color: '#1e293b',
                      fontSize: '0.95rem',
                    }}
                  >
                    Split View:
                  </span>
                  <select
                    value={splitViewTab}
                    onChange={(e) => setSplitViewTab(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      cursor: 'pointer',
                      backgroundColor: '#f8fafc',
                      color: '#0f172a',
                    }}
                  >
                    <option value="templates">Customize Template</option>
                    <option value="saved">Saved Reports</option>
                    <option value="vault">Sono Vault</option>
                    <option value="share">Sono Share</option>
                    <option value="sono-ai">Sono AI</option>
                  </select>
                </div>
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    position: 'relative',
                    minWidth: 0,
                  }}
                >
                  {splitViewTab === 'templates' && (
                    <CustomizeTemplate onBack={handleBack} />
                  )}
                  {splitViewTab === 'saved' && (
                    <SavedReports userProfile={userProfile} onBack={handleBack} />
                  )}
                  {splitViewTab === 'vault' && (
                    <SonoVault
                      files={vaultFiles}
                      onAddFiles={onAddFiles}
                      onBack={handleBack}
                    />
                  )}
                  {splitViewTab === 'share' && (
                    <SonoShare userProfile={userProfile} onBack={handleBack} />
                  )}
                  {splitViewTab === 'sono-ai' && (
                    <SonoAI userProfile={userProfile} />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;